/**
 * Serviço de Download de eBooks
 * Gerencia download de PDFs após pagamento confirmado
 */

import { getPdfPath, getPdfFileName, hasPdf } from '../utils/ebookPdfMapping';

class EbookDownloadService {
  /**
   * Inicializar serviço
   */
  constructor() {
    this.downloadHistory = this.getDownloadHistory();
    this.downloadLog = this.getDownloadLog();
    
    // Validar e corrigir registros inválidos
    this.validateAndCleanHistory();
  }

  /**
   * Validar e limpar registros inválidos (contador > maxDownloads)
   */
  validateAndCleanHistory() {
    this.downloadHistory = this.downloadHistory.map((item) => {
      // Se contador exceder máximo, resetar para 0
      if (item.downloadCount > item.maxDownloads) {
        console.warn(`⚠️ Contador inválido para ${item.ebookId}: ${item.downloadCount} > ${item.maxDownloads}. Resetando.`);
        return {
          ...item,
          downloadCount: 0,
        };
      }
      return item;
    });

    // Se houve mudanças, salvar
    if (this.downloadHistory.some((item, i) => {
      const original = this.getDownloadHistory()[i];
      return original && item.downloadCount !== original.downloadCount;
    })) {
      this.saveDownloadHistory();
      console.log(`✅ Histórico validado e corrigido`);
    }
  }

  /**
   * Obter histórico de downloads do localStorage
   * @returns {Array}
   */
  getDownloadHistory() {
    try {
      const history = localStorage.getItem('ebook_download_history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Erro ao recuperar histórico de downloads:', error);
      return [];
    }
  }

  /**
   * Salvar histórico de downloads
   */
  saveDownloadHistory() {
    try {
      localStorage.setItem('ebook_download_history', JSON.stringify(this.downloadHistory));
    } catch (error) {
      console.error('Erro ao salvar histórico de downloads:', error);
    }
  }

  /**
   * Obter log de downloads
   * @returns {Array}
   */
  getDownloadLog() {
    try {
      const log = localStorage.getItem('ebook_download_log');
      return log ? JSON.parse(log) : [];
    } catch (error) {
      console.error('Erro ao recuperar log de downloads:', error);
      return [];
    }
  }

  /**
   * Registrar download em log (auditoria)
   * @param {Object} downloadInfo - Informações do download
   */
  logDownload(downloadInfo) {
    const logEntry = {
      ebookId: downloadInfo.ebookId,
      ebookTitle: downloadInfo.ebookTitle,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      ipInfo: 'Frontend only - sem informações de IP',
    };

    this.downloadLog.push(logEntry);

    // Manter apenas os últimos 500 logs
    if (this.downloadLog.length > 500) {
      this.downloadLog = this.downloadLog.slice(-500);
    }

    try {
      localStorage.setItem('ebook_download_log', JSON.stringify(this.downloadLog));
    } catch (error) {
      console.error('Erro ao salvar log de downloads:', error);
    }
  }

  /**
   * Registrar que um eBook pode ser baixado
   * @param {string} ebookId - ID do eBook
   * @param {string} ebookTitle - Título do eBook
   * @param {Object} paymentInfo - Informações de pagamento
   * @returns {Object} - {success: boolean, message: string, downloadUrl: string}
   */
  authorizeDownload(ebookId, ebookTitle, paymentInfo = {}) {
    try {
      // Verificar se eBook tem PDF mapeado
      if (!hasPdf(ebookId)) {
        return {
          success: false,
          message: '❌ eBook não possui PDF disponível para download',
          downloadUrl: null,
        };
      }

      // Verificar se já existe autorização para este eBook
      const existingIndex = this.downloadHistory.findIndex((item) => item.ebookId === ebookId);

      if (existingIndex !== -1) {
        // Atualizar registro existente ao invés de criar novo
        this.downloadHistory[existingIndex] = {
          ...this.downloadHistory[existingIndex],
          authorizedAt: new Date().toISOString(),
          expiresAt: this.getExpirationDate(30), // Renovar expiração
          paymentMethod: paymentInfo.method || 'desconhecido',
          transactionId: paymentInfo.transactionId || this.downloadHistory[existingIndex].transactionId,
          // Manter downloadCount e maxDownloads
        };
      } else {
        // Criar novo registro de download autorizado
        const downloadRecord = {
          ebookId,
          ebookTitle,
          authorizedAt: new Date().toISOString(),
          expiresAt: this.getExpirationDate(30), // Expiração em 30 dias
          paymentMethod: paymentInfo.method || 'desconhecido',
          transactionId: paymentInfo.transactionId || null,
          downloadCount: 0,
          maxDownloads: 999999, // ILIMITADO - sem restrição de downloads
        };

        // Adicionar ao histórico
        this.downloadHistory.push(downloadRecord);
      }

      this.saveDownloadHistory();

      // Registrar em log
      this.logDownload({
        ebookId,
        ebookTitle,
        action: 'authorize',
        paymentMethod: paymentInfo.method,
      });

      return {
        success: true,
        message: `✅ Download autorizado para "${ebookTitle}"`,
        downloadUrl: getPdfPath(ebookId),
        downloadRecord: this.downloadHistory[existingIndex !== -1 ? existingIndex : this.downloadHistory.length - 1],
      };
    } catch (error) {
      console.error('Erro ao autorizar download:', error);
      return {
        success: false,
        message: '❌ Erro ao autorizar download',
        downloadUrl: null,
      };
    }
  }

  /**
   * Autorizar downloads de múltiplos eBooks (após pagamento)
   * @param {Array} ebooks - Array de eBooks [{id, title}, ...]
   * @param {Object} paymentInfo - Informações de pagamento
   * @returns {Object} - {success: boolean, authorizedEbooks: Array, failedEbooks: Array}
   */
  authorizeMultipleDownloads(ebooks, paymentInfo = {}) {
    const results = {
      success: true,
      authorizedEbooks: [],
      failedEbooks: [],
      message: '',
    };

    ebooks.forEach((ebook) => {
      const result = this.authorizeDownload(ebook.id, ebook.titulo || ebook.title, paymentInfo);

      if (result.success) {
        results.authorizedEbooks.push({
          ebookId: ebook.id,
          ebookTitle: ebook.titulo || ebook.title,
          downloadUrl: result.downloadUrl,
        });
      } else {
        results.failedEbooks.push({
          ebookId: ebook.id,
          ebookTitle: ebook.titulo || ebook.title,
          reason: result.message,
        });
      }
    });

    results.message = `✅ ${results.authorizedEbooks.length} eBooks autorizados | ❌ ${results.failedEbooks.length} falharam`;

    return results;
  }

  /**
   * Verificar se tem autorização de download
   * @param {string} ebookId - ID do eBook
   * @returns {boolean}
   */
  hasDownloadAuthorization(ebookId) {
    const record = this.downloadHistory.find(
      (item) => item.ebookId === ebookId && !this.isExpired(item.expiresAt)
    );
    // Sem restrição de número de downloads - apenas verifica se existe e não expirou
    return !!record;
  }

  /**
   * Obter registro de download
   * @param {string} ebookId - ID do eBook
   * @returns {Object|null}
   */
  getDownloadRecord(ebookId) {
    const record = this.downloadHistory.find((item) => item.ebookId === ebookId);
    return record ? { ...record, isExpired: this.isExpired(record.expiresAt) } : null;
  }

  /**
   * Gerar token simples para autenticação de download
   * @returns {string}
   */
  generateDownloadToken() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(7);
    return `${timestamp}-${random}`;
  }

  /**
   * Executar download de um eBook (via rota protegida)
   * @param {string} ebookId - ID do eBook
   * @param {string} ebookTitle - Título do eBook
   * @returns {Object} - {success: boolean, message: string}
   */
  downloadEbook(ebookId, ebookTitle) {
    try {
      // Verificar autorização
      if (!this.hasDownloadAuthorization(ebookId)) {
        return {
          success: false,
          message: '❌ Você não tem autorização para download deste eBook',
        };
      }

      // Obter nome do arquivo
      const pdfFileName = getPdfFileName(ebookId);

      if (!pdfFileName) {
        return {
          success: false,
          message: '❌ PDF não encontrado',
        };
      }

      console.log(`📥 Iniciando download protegido: ${ebookTitle}`);
      console.log(`   eBook ID: ${ebookId}`);
      console.log(`   Nome: ${pdfFileName}`);

      // Incrementar contador de downloads - CRIAR CÓPIA PARA EVITAR MUTAÇÃO
      const recordIndex = this.downloadHistory.findIndex((item) => item.ebookId === ebookId);
      if (recordIndex !== -1) {
        // Criar novo objeto com as mudanças (não mutar direto)
        const updatedRecord = {
          ...this.downloadHistory[recordIndex],
          downloadCount: this.downloadHistory[recordIndex].downloadCount + 1,
          lastDownloadAt: new Date().toISOString(),
        };
        // Substituir na array
        this.downloadHistory[recordIndex] = updatedRecord;
        this.saveDownloadHistory();
      }

      // Registrar em log
      this.logDownload({
        ebookId,
        ebookTitle,
        action: 'download',
        timestamp: new Date().toISOString(),
      });

      // Gerar token para autenticação
      const token = this.generateDownloadToken();

      // Construir URL da rota protegida
      const downloadUrl = `/api/download/${ebookId}?token=${encodeURIComponent(token)}`;

      console.log(`📥 URL de download: ${downloadUrl}`);

      // Fazer download via rota protegida
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = pdfFileName;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      
      // Usar setTimeout para garantir que o elemento foi adicionado ao DOM
      setTimeout(() => {
        link.click();
        
        console.log(`✅ Download iniciado com sucesso: ${pdfFileName}`);
        
        // Remover link após download
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }, 50);

      return {
        success: true,
        message: `✅ Download iniciado: ${pdfFileName}`,
        fileName: pdfFileName,
      };
    } catch (error) {
      console.error('❌ Erro ao fazer download:', error);
      console.error('   Detalhes:', error.message);
      console.error('   Stack:', error.stack);
      
      return {
        success: false,
        message: `❌ Erro ao fazer download: ${error.message || 'Arquivo não encontrado'}`,
      };
    }
  }

  /**
   * Obter informações de download de um eBook
   * @param {string} ebookId - ID do eBook
   * @returns {Object}
   */
  getDownloadInfo(ebookId) {
    const record = this.getDownloadRecord(ebookId);

    if (!record) {
      return {
        authorized: false,
        message: 'Sem autorização de download',
        downloadCount: 0,
        maxDownloads: 0,
      };
    }

    const isExpired = this.isExpired(record.expiresAt);

    return {
      authorized: !isExpired && record.downloadCount < record.maxDownloads,
      isExpired,
      downloadCount: record.downloadCount,
      maxDownloads: record.maxDownloads,
      downloadsRemaining: Math.max(0, record.maxDownloads - record.downloadCount),
      expiresAt: record.expiresAt,
      authorizedAt: record.authorizedAt,
      message: isExpired
        ? '⏰ Autorização expirada'
        : record.downloadCount >= record.maxDownloads
        ? '🚫 Limite de downloads atingido'
        : `✅ Autorizado (${record.downloadCount}/${record.maxDownloads} downloads usados)`,
    };
  }

  /**
   * Obter downloads autorizados e não expirados (com deduplicação por ebookId)
   * @returns {Array}
   */
  getAuthorizedDownloads() {
    const deduped = new Map();
    
    this.downloadHistory
      .filter((item) => !this.isExpired(item.expiresAt))
      .filter((item) => item.downloadCount < item.maxDownloads)
      .forEach((item) => {
        // Manter apenas um registro por ebookId (o mais recente)
        if (!deduped.has(item.ebookId) || 
            new Date(item.authorizedAt) > new Date(deduped.get(item.ebookId).authorizedAt)) {
          deduped.set(item.ebookId, item);
        }
      });
    
    return Array.from(deduped.values());
  }

  /**
   * Obter estatísticas de downloads
   * @returns {Object}
   */
  getDownloadStats() {
    const totalAuthorized = this.downloadHistory.length;
    const totalExpired = this.downloadHistory.filter((item) =>
      this.isExpired(item.expiresAt)
    ).length;
    const totalActive = totalAuthorized - totalExpired;
    const totalDownloads = this.downloadHistory.reduce((sum, item) => sum + item.downloadCount, 0);

    return {
      totalAuthorized,
      totalActive,
      totalExpired,
      totalDownloads,
      logs: this.downloadLog.length,
      message: `Total: ${totalAuthorized} autorizados | Ativos: ${totalActive} | Expirados: ${totalExpired} | Downloads: ${totalDownloads}`,
    };
  }

  /**
   * Limpar histórico de downloads expirados
   * @returns {number} - Número de registros removidos
   */
  cleanupExpiredDownloads() {
    const beforeLength = this.downloadHistory.length;
    this.downloadHistory = this.downloadHistory.filter(
      (item) => !this.isExpired(item.expiresAt)
    );
    this.saveDownloadHistory();
    return beforeLength - this.downloadHistory.length;
  }

  /**
   * Limpar histórico completo
   */
  clearAll() {
    this.downloadHistory = [];
    this.downloadLog = [];
    localStorage.removeItem('ebook_download_history');
    localStorage.removeItem('ebook_download_log');
  }

  /**
   * ========== HELPER METHODS ==========
   */

  /**
   * Calcular data de expiração
   * @param {number} days - Número de dias
   * @returns {string} - Data em formato ISO
   */
  getExpirationDate(days = 30) {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    return expirationDate.toISOString();
  }

  /**
   * Verificar se uma data expirou
   * @param {string} expirationDate - Data em formato ISO
   * @returns {boolean}
   */
  isExpired(expirationDate) {
    return new Date(expirationDate) < new Date();
  }

  /**
   * Formatar tamanho do arquivo
   * @param {number} sizeInMB - Tamanho em MB
   * @returns {string}
   */
  formatFileSize(sizeInMB) {
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(1)} GB`;
    }
    return `${sizeInMB.toFixed(1)} MB`;
  }
}

// Exportar singleton
export const ebookDownloadService = new EbookDownloadService();

export default ebookDownloadService;
